import React, { memo, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Calendar, ShoppingBag, Trash2, Warehouse } from 'lucide-react';

import { DetailBreadcrumb } from '../../../../components/ui/DetailBreadcrumb';
import { DetailHero } from '../../../../components/ui/DetailHero';
import { Button } from '../../../../components/ui/Button';
import { ConfirmDialog } from '../../../../components/ui/ConfirmDialog';
import { ErrorState } from '../../../../components/ui/ErrorState';
import { notify } from '../../../../features/feedback/notify';

import {
  createEcommerceIntegration,
  deleteEcommerceIntegration,
  fetchEcommerceIntegrations,
  fetchEcommerceWarehouses,
  updateEcommerceIntegration,
} from '../../../../features/ecommerces/ecommercesThunks';
import { ECOMMERCE_NOTICE } from '../../../../features/ecommerces/ecommercesFeedback';
import {
  selectAvailableEcommerces,
  selectIntegrationById,
  selectPlatformAvailability,
  selectWarehouseNames,
} from '../../../../features/ecommerces/ecommercesSelectors';
import {
  getConnectionStatus,
  getPlatformBySlug,
  getPlatformDisplayName,
  getPlatformSlug,
  getSelectableWarehouses,
  isMeliPending,
  UNAVAILABLE_REASON,
} from '../../../../features/ecommerces/ecommercesPlatforms';
import { getOauthReturnNotice } from '../../../../features/ecommerces/oauthReturn';

import EcommerceForm from './components/EcommerceForm';
import EcommerceStatusBadge from './components/EcommerceStatusBadge';
import MeliPendingBanner from './components/MeliPendingBanner';

const ECOMMERCE_LIST_PATH = '/configuration/ecommerce';

const UNAVAILABLE_MESSAGES = {
  [UNAVAILABLE_REASON.ALL_WAREHOUSES]: 'Todas tus bodegas ya tienen una cuenta de Mercado Libre.',
  [UNAVAILABLE_REASON.NO_WAREHOUSES]: 'Necesitas una bodega para integrar Mercado Libre.',
};

/**
 * The forms copy their props into state once. The key makes them start over
 * when the stored integration changes underneath — after saving or on the way
 * back from OAuth — and not while the seller types. `updated_at` cannot do it:
 * the backend never moves it.
 */
const getFormKey = (integration, platform) =>
  integration.id
    ? `${integration.id}:${JSON.stringify([integration.warehouse, integration.configuration])}`
    : `new:${platform}`;

/**
 * `/configuration/ecommerce/:integrationId` and `/configuration/ecommerce/new/:platform`.
 *
 * In "new" mode nothing exists yet: the template is on screen and "Finalizar
 * integración" creates it, then the page moves to its address. Shopify and
 * Mercado Libre create their record when they start OAuth instead, and the
 * return lands here through `resolveOauthReturn`.
 *
 * There is no GET by id in the backend, so both modes read the listing.
 */
const EcommerceDetail = () => {
  const { integrationId, platform } = useParams();
  const isNew = Boolean(platform);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();

  const { integrations, warehouses } = useSelector((state) => state.ecommerces);
  const catalog = useSelector(selectAvailableEcommerces);
  const availability = useSelector(selectPlatformAvailability);
  const warehouseNames = useSelector(selectWarehouseNames);
  const stored = useSelector(selectIntegrationById(integrationId));

  // Whether this visit's own request came back. Until then a missing
  // integration may just be a listing loaded before it existed.
  const [fetched, setFetched] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const platformInfo = isNew ? getPlatformBySlug(platform) : null;
  const template = isNew
    ? catalog.find((ecommerce) => getPlatformSlug(ecommerce.name) === platform) || null
    : null;
  const integration = isNew ? template : stored;

  useEffect(() => {
    let active = true;
    Promise.all([
      dispatch(fetchEcommerceIntegrations()),
      dispatch(fetchEcommerceWarehouses()),
    ]).finally(() => {
      if (active) setFetched(true);
    });
    return () => {
      active = false;
    };
  }, [dispatch]);

  // "new" only for a platform the seller can still add: an unknown slug goes
  // back to the listing, and a platform already integrated opens that one.
  useEffect(() => {
    if (!isNew) return;
    if (!platformInfo) {
      navigate(ECOMMERCE_LIST_PATH, { replace: true });
      return;
    }
    if (!fetched) return;

    const { available, existingId, reason } = availability[platform] || {};
    if (available) return;
    if (existingId) {
      navigate(`${ECOMMERCE_LIST_PATH}/${existingId}`, { replace: true });
      return;
    }
    dispatch(notify.error(UNAVAILABLE_MESSAGES[reason], { testId: ECOMMERCE_NOTICE }));
    navigate(ECOMMERCE_LIST_PATH, { replace: true });
  }, [isNew, platform, platformInfo, fetched, availability, navigate, dispatch]);

  // Shopify's callback answers in the query. Read once, then dropped from the
  // address so a reload does not announce it again.
  useEffect(() => {
    const returnNotice = getOauthReturnNotice(search);
    if (!returnNotice) return;
    dispatch(notify[returnNotice.type](returnNotice.message, { testId: ECOMMERCE_NOTICE }));
    navigate(pathname, { replace: true });
  }, [search, pathname, navigate, dispatch]);

  const handleSave = async (formData) => {
    // The forms never checked it; the backend rejects a missing warehouse
    // with a message that does not say so.
    if (!formData.warehouse) {
      dispatch(notify.error('Selecciona la bodega asociada.', { testId: ECOMMERCE_NOTICE }));
      return;
    }

    if (isNew) {
      const action = await dispatch(createEcommerceIntegration(formData));
      if (action.meta.requestStatus !== 'fulfilled') return;
      navigate(
        action.payload?.id ? `${ECOMMERCE_LIST_PATH}/${action.payload.id}` : ECOMMERCE_LIST_PATH,
        { replace: true }
      );
      return;
    }

    dispatch(updateEcommerceIntegration({ id: formData.id, data: formData }));
  };

  const handleDelete = async () => {
    setDeleting(true);
    const action = await dispatch(deleteEcommerceIntegration(stored.id));
    setDeleting(false);
    setIsDeleteOpen(false);

    if (action.meta.requestStatus === 'fulfilled') navigate(ECOMMERCE_LIST_PATH);
  };

  if (!integration) {
    if (!fetched && !isNew) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-4 border-ninja-green border-t-transparent rounded-full animate-spin" />
        </div>
      );
    }

    return (
      <div data-testid="ecommerce-detail-page" className="bg-white dark:bg-[#1a1a1a] rounded-2xl border border-border">
        <ErrorState
          title="No encontramos esta integración"
          description="Puede que se haya eliminado. Vuelve al listado para ver tus integraciones."
        >
          <Button variant="ghost" onClick={() => navigate(ECOMMERCE_LIST_PATH)}>
            Volver a Integraciones
          </Button>
        </ErrorState>
      </div>
    );
  }

  const displayName = platformInfo?.name || getPlatformDisplayName(integration.name);
  const warehouseName = warehouseNames[String(integration.warehouse)];
  const selectableWarehouses = getSelectableWarehouses({
    integration,
    isEdit: !isNew,
    warehouses,
    integrations,
  });
  const showMeliPending = !isNew && isMeliPending(integration);

  return (
    <div
      data-testid="ecommerce-detail-page"
      data-platform={getPlatformSlug(integration.name)}
      className="space-y-8 animate-in fade-in duration-300"
    >
      <DetailBreadcrumb
        backLabel="Volver a Integraciones"
        to={ECOMMERCE_LIST_PATH}
        current={isNew ? `Nueva integración ${displayName}` : displayName}
      />

      <DetailHero
        testId="ecommerce-detail-hero"
        media={
          integration.image ? (
            <img src={integration.image} alt="" className="w-full h-full object-contain p-3" />
          ) : (
            <ShoppingBag size={40} strokeWidth={1.5} />
          )
        }
        mediaClassName={integration.image ? 'bg-white border-border' : undefined}
        title={displayName}
        badges={
          isNew ? (
            <span
              data-testid="ecommerce-status"
              data-status="new"
              className="px-2.5 py-1 rounded-full text-xs font-bold border bg-ninja-gray dark:bg-[#2d2d2d] text-text-secondary border-border"
            >
              Nueva
            </span>
          ) : (
            <EcommerceStatusBadge status={getConnectionStatus(integration)} />
          )
        }
        meta={
          isNew ? (
            <span>Completa la configuración para empezar a recibir las órdenes de tu tienda.</span>
          ) : (
            <>
              <span className="flex items-center gap-1.5">
                <Warehouse size={14} /> {warehouseName || `Bodega #${integration.warehouse}`}
              </span>
              <span>ID {integration.id}</span>
              {integration.created_at && (
                <span className="flex items-center gap-1.5">
                  <Calendar size={14} /> Creada el{' '}
                  {new Date(integration.created_at).toLocaleDateString('es-CL')}
                </span>
              )}
            </>
          )
        }
        actions={
          !isNew && (
            <Button
              variant="danger"
              icon={Trash2}
              onClick={() => setIsDeleteOpen(true)}
              data-testid="ecommerce-delete"
            >
              Eliminar
            </Button>
          )
        }
      />

      {showMeliPending ? (
        <MeliPendingBanner integration={integration} />
      ) : (
        <section className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-6 sm:p-8 shadow-sm border border-border">
          <EcommerceForm
            key={getFormKey(integration, platform)}
            ecommerce={integration}
            isEdit={!isNew}
            warehouses={selectableWarehouses}
            onSave={handleSave}
          />
        </section>
      )}

      {!isNew && (
        <ConfirmDialog
          isOpen={isDeleteOpen}
          onClose={() => !deleting && setIsDeleteOpen(false)}
          onConfirm={handleDelete}
          title="Eliminar integración"
          message={`Se elimina la integración de ${displayName}. Dejarás de recibir sus órdenes en NinjaHubs.`}
          loading={deleting}
          testId="ecommerce-delete-dialog"
        />
      )}
    </div>
  );
};

export default memo(EcommerceDetail);
